// ABOUTME: Compares two AuthenticateSmartcheckSessionDto responses (V1 vs V2) and returns all field-level differences.

import type { AuthenticateSmartcheckSessionDto } from '../types/authenticateSmartcheckSessionDto'

interface FieldDifference {
	v1: string
	v2: string
}

export interface ComparisonResult {
	isMatch: boolean
	differenceCount: number
	differences: Record<string, FieldDifference>
}

const flattenObject = (
	obj: Record<string, any>,
	prefix?: string,
	result: Record<string, string> = {},
): Record<string, string> => {
	for (const key of Object.keys(obj)) {
		const propName = prefix ? prefix + '.' + key : key
		const value = obj[key]
		if (value !== null && typeof value === 'object') {
			flattenObject(value, propName, result)
		} else {
			result[propName] = JSON.stringify(value)
		}
	}
	return result
}

export const compareAuthenticationResponses = (
	v1: AuthenticateSmartcheckSessionDto,
	v2: AuthenticateSmartcheckSessionDto,
	excludeKeys: string[] = ['token', 'logo', 'applicantSessionId'],
): ComparisonResult => {
	const v1Copy = { ...v1 } as Record<string, any>
	const v2Copy = { ...v2 } as Record<string, any>

	for (const key of excludeKeys) {
		delete v1Copy[key]
		delete v2Copy[key]
	}

	const flatV1 = flattenObject(v1Copy)
	const flatV2 = flattenObject(v2Copy)

	const allKeys = new Set([...Object.keys(flatV1), ...Object.keys(flatV2)])
	const differences: Record<string, FieldDifference> = {}

	for (const key of allKeys) {
		const val1 = flatV1[key] ?? JSON.stringify(undefined)
		const val2 = flatV2[key] ?? JSON.stringify(undefined)
		if (val1 !== val2) {
			differences[key] = { v1: val1, v2: val2 }
		}
	}

	return {
		isMatch: Object.keys(differences).length === 0,
		differenceCount: Object.keys(differences).length,
		differences,
	}
}
